/**
 * AETERNA SSE 客户端
 *
 * 基于 fetch + ReadableStream 手动解析 SSE，
 * 不使用 EventSource（不支持自定义 Authorization header）。
 *
 * 用法:
 *   for await (const event of createSSEStream('/chat/sessions/xxx/messages/stream', { body })) {
 *     console.log(event.data);
 *   }
 */

import { API_BASE } from './config';
import { getAuthFns } from './http';

// ── SSE 事件类型 ──

export interface SSEEvent {
  /** 事件类型（默认 'message'） */
  event: string;
  /** 事件数据 */
  data: string;
  /** 事件 ID */
  id?: string;
}

// ── SSE 流创建 ──

interface SSEOptions {
  body?: unknown;
  method?: string;
  signal?: AbortSignal;
  /** 获取 Authorization token */
  getToken?: () => string | null;
}

/**
 * 创建 SSE 流
 *
 * 返回 AsyncIterable<SSEEvent>，可以用 for-await-of 消费。
 */
export async function* createSSEStream(
  path: string,
  options: SSEOptions = {},
): AsyncGenerator<SSEEvent, void, unknown> {
  const { body, method = 'POST', signal, getToken } = options;
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
  };

  if (body) {
    headers['Content-Type'] = 'application/json';
  }

  const token = getToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(url, {
    method,
    headers,
    body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
    signal,
  });

  if (!response.ok) {
    if (response.status === 401) {
      const { refreshToken, onLogout, getToken } = getAuthFns();
      const refreshed = await refreshToken();
      if (refreshed) {
        const newToken = getToken();
        if (newToken) headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, {
          method,
          headers,
          body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
          signal,
        });
        if (retryResponse.ok) {
          response = retryResponse;
        } else {
          onLogout();
          throw new Error('SSE 认证失败，请重新登录');
        }
      } else {
        onLogout();
        throw new Error('SSE 认证失败，请重新登录');
      }
    } else {
      const text = await response.text().catch(() => '');
      throw new Error(`SSE 连接失败 (${response.status}): ${text}`);
    }
  }

  if (!response.body) {
    throw new Error('SSE 响应没有 body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // 按双换行分割事件（兼容 \n\n 和 \r\n\r\n）
      const parts = buffer.split(/\r?\n\r?\n/);
      // 最后一段可能不完整，保留
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const event = parseSSEEvent(part);
        if (event) {
          yield event;
        }
      }
    }

    // 处理缓冲区中的最后一个事件
    if (buffer.trim()) {
      const event = parseSSEEvent(buffer);
      if (event) {
        yield event;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** 解析单个 SSE 事件块 */
function parseSSEEvent(raw: string): SSEEvent | null {
  const lines = raw.split('\n');
  let event = 'message';
  let data = '';
  let id: string | undefined;

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      data += (data ? '\n' : '') + line.slice(5).trim();
    } else if (line.startsWith('id:')) {
      id = line.slice(3).trim();
    }
    // 忽略注释（以 : 开头）和空行
  }

  if (!data) return null;

  return { event, data, id };
}
